<template>
  <v-tooltip
    v-if="aiLabel && interactive && !overlay"
    location="top" :open-delay="300" content-class="text-neutral0 bg-neutral900" :disabled="mobile"
  >
    <template #activator="{ props: tooltipProps }">
      <BrandButton
        class="text-neutral900 px-1"
        size="small" flat variant="text"
        :aria-label="tooltip"
        v-bind="tooltipProps"
        v-on="stopEvents"
      >
        <template #prepend>
          <PhInfo class="text-h6" />
        </template>
        <span class="text-caption font-weight-medium">{{ t('ai_label.badge') }}</span>
      </BrandButton>
    </template>
    <span>{{ tooltip }}</span>
  </v-tooltip>

  <button
    v-else-if="aiLabel && interactive"
    type="button"
    class="ai-label-badge ai-label-badge--overlay"
    :aria-label="tooltip"
    v-on="stopEvents"
  >
    <PhInfo class="text-h6" />
    <span class="text-caption font-weight-medium">{{ t('ai_label.badge') }}</span>
  </button>

  <span v-else-if="aiLabel" class="ai-label-badge" :class="{ 'ai-label-badge--overlay': overlay }">
    <PhInfo class="text-h6" />
    <span class="text-caption font-weight-medium">{{ t('ai_label.badge') }}</span>
  </span>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { PhInfo } from '@phosphor-icons/vue'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import { aiLabelInfoRoute } from '@/utils/ai-label'

const props = defineProps({
  // { kind, types } or null; nothing is rendered without a label
  aiLabel: {
    type: Object,
    default: null,
  },
  // Dark pill drawn over a thumbnail (tier cards); positioned bottom-left of the nearest relative parent
  overlay: Boolean,
  // false = plain, inert badge (e.g. tier cards on the presentation stage)
  interactive: {
    type: Boolean,
    default: true,
  },
})

const { t } = useI18n()
const { mobile } = useDisplay()
const router = useRouter()

const tooltip = computed(() => t('ai_label.badge_tooltip'))

// The badge sits on clickable, selectable and draggable surfaces: nothing it receives may reach them
const stop = event => event.stopPropagation()
const stopEvents = {
  click: event => {
    event.stopPropagation()
    event.preventDefault()
    router.push(aiLabelInfoRoute(props.aiLabel))
  },
  pointerdown: stop,
  mousedown: stop,
  touchstart: stop,
}
</script>

<style scoped>
.ai-label-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.ai-label-badge--overlay {
  position: absolute;
  left: 4px;
  bottom: 4px;
  padding: 0 4px;
  border-radius: 9999px;
  background: rgba(var(--v-theme-neutral900), 0.75);
  color: rgb(var(--v-theme-neutral0));
}
</style>
